import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, ClipboardList, Inbox, Eye, ShieldCheck, Building2, Factory,
  LogOut, Menu, ShieldAlert, Settings2, ChevronLeft, CreditCard, Newspaper, Wallet, FileCog, Heart, Users, Mail, BadgePercent, Gift, MessageCircleQuestion, MessageCircle, Receipt, Scale,
} from 'lucide-react';
import { Milestone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import GoToTop from './GoToTop';
import UserAvatar from './UserAvatar';
import NotificationBell from './NotificationBell';
import AnnouncementBanner from './AnnouncementBanner';

const userNav = [
  { to: '/dashboard', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/dashboard/listings', label: 'My Listings', icon: ClipboardList },
  { to: '/dashboard/inquiries', label: 'Inquiries Inbox', icon: Inbox },
  { to: '/dashboard/reviews', label: 'My Reviews', icon: MessageCircle },
  { to: '/dashboard/favorites', label: 'Favorites', icon: Heart },
  { to: '/dashboard/comparisons', label: 'Saved Comparisons', icon: Scale },
  { to: '/dashboard/following', label: 'Following', icon: Users },
  { to: '/dashboard/reveal-log', label: 'Reveal Log', icon: Eye },
  { to: '/dashboard/verification', label: 'Verification', icon: ShieldCheck },
  { to: '/dashboard/billing', label: 'Billing & Plans', icon: CreditCard },
  { to: '/dashboard/invoices', label: 'Invoices', icon: Receipt },
  { to: '/dashboard/invite', label: 'Invite & Earn', icon: Gift },
  { to: '/dashboard/profile', label: 'Profile / Company', icon: Building2 },
  { to: '/dashboard/notifications', label: 'Email preferences', icon: Mail },
];

const modNav = [
  { to: '/moderator', label: 'Moderator Panel', icon: ShieldAlert, end: true },
];

const adminNav = [
  { to: '/admin', label: 'Overview', icon: Settings2, end: true },
  { to: '/admin/cms', label: 'CMS & Locations', icon: FileCog },
  { to: '/admin/redirects', label: 'Redirects', icon: Milestone },
  { to: '/admin/blog', label: 'Blog', icon: Newspaper },
  { to: '/admin/emails', label: 'Emails', icon: Mail },
  { to: '/admin/promotions', label: 'Promotions', icon: BadgePercent },
  { to: '/admin/referrals', label: 'Referrals', icon: Gift },
  { to: '/admin/feedback', label: 'Feedback', icon: MessageCircleQuestion },
  { to: '/admin/payments', label: 'Payments', icon: Wallet },
];

const SidebarLinks = ({ items, onNav }) => (
  <nav className='space-y-1'>
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onNav}
        data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
            isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
          }`
        }
      >
        <Icon className='h-4 w-4' />
        <span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

const DashboardLayout = ({ moderator, admin }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = admin ? adminNav : moderator ? modNav : userNav;
  const heading = admin ? 'Admin' : moderator ? 'Moderation' : 'Dashboard';

  return (
    <div className='flex min-h-screen bg-background'>
      {/* Desktop sidebar */}
      <aside className='hidden md:flex md:w-64 flex-col border-r bg-card sticky top-0 h-screen'>
        <div className='h-16 flex items-center gap-2 px-4 border-b'>
          <span className='inline-flex h-8 w-8 items-center justify-center rounded-md bg-white border overflow-hidden'>
            <img src='/indusfact-logo.png' alt='IndusFact' className='h-7 w-7 object-contain' />
          </span>
          <div className='leading-tight'>
            <div className='font-semibold text-sm'>IndusFact</div>
            <div className='text-xs text-muted-foreground'>{heading}</div>
          </div>
        </div>
        <div className='p-3 flex-1 overflow-y-auto'>
          <SidebarLinks items={items} />
          <div className='mt-6 border-t pt-4'>
            <p className='px-3 pb-2 text-xs uppercase tracking-wider text-muted-foreground'>Quick</p>
            <Button variant='outline' className='w-full justify-start' onClick={() => navigate('/')} data-testid='sidebar-back-to-site'>
              <ChevronLeft className='h-4 w-4 mr-1' /> Back to site
            </Button>
          </div>
        </div>
        <div className='p-3 border-t'>
          <div className='flex items-center gap-2 mb-2 text-sm'>
            <UserAvatar
              name={user?.name}
              photo={user?.profile_photo_thumb || user?.profile_photo}
              updatedAt={user?.profile_photo_updated_at}
              size={32}
              data-testid='sidebar-user-avatar'
            />
            <div className='flex-1 min-w-0'>
              <div className='truncate font-medium text-sm'>{user?.name}</div>
              <div className='truncate text-xs text-muted-foreground'>{user?.role}</div>
            </div>
          </div>
          <Button variant='ghost' size='sm' className='w-full justify-start' onClick={() => { logout(); navigate('/'); }} data-testid='sidebar-logout'>
            <LogOut className='h-4 w-4 mr-2' /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile */}
      <div className='flex-1 flex flex-col'>
        <div className='md:hidden h-14 flex items-center justify-between px-4 border-b bg-card'>
          <div className='flex items-center gap-2'>
            <span className='inline-flex h-7 w-7 items-center justify-center rounded-md bg-white border overflow-hidden'>
              <img src='/indusfact-logo.png' alt='IndusFact' className='h-6 w-6 object-contain' />
            </span>
            <span className='font-semibold text-sm'>IndusFact {heading}</span>
          </div>
          <div className='flex items-center gap-1'>
            <NotificationBell />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant='ghost' size='icon' data-testid='dashboard-mobile-menu'>
                  <Menu className='h-5 w-5' />
                </Button>
              </SheetTrigger>
            <SheetContent side='left' className='w-72 flex flex-col p-0' data-testid='dashboard-mobile-sheet'>
              <div className='h-14 flex items-center gap-2 px-4 border-b'>
                <span className='inline-flex h-7 w-7 items-center justify-center rounded-md bg-white border overflow-hidden'>
                  <img src='/indusfact-logo.png' alt='IndusFact' className='h-6 w-6 object-contain' />
                </span>
                <div className='leading-tight'>
                  <div className='font-semibold text-sm'>IndusFact</div>
                  <div className='text-xs text-muted-foreground'>{heading}</div>
                </div>
              </div>
              <div className='flex-1 overflow-y-auto p-3'>
                <SidebarLinks items={items} onNav={() => setOpen(false)} />
                <div className='mt-6 border-t pt-4'>
                  <p className='px-3 pb-2 text-xs uppercase tracking-wider text-muted-foreground'>Quick</p>
                  <Button
                    variant='outline'
                    className='w-full justify-start'
                    onClick={() => { setOpen(false); navigate('/'); }}
                    data-testid='sidebar-back-to-site-mobile'
                  >
                    <ChevronLeft className='h-4 w-4 mr-1' /> Back to site
                  </Button>
                </div>
              </div>
              <div className='p-3 border-t'>
                <div className='flex items-center gap-2 mb-2 text-sm'>
                  <UserAvatar
                    name={user?.name}
                    photo={user?.profile_photo_thumb || user?.profile_photo}
                    updatedAt={user?.profile_photo_updated_at}
                    size={32}
                  />
                  <div className='flex-1 min-w-0'>
                    <div className='truncate font-medium text-sm'>{user?.name}</div>
                    <div className='truncate text-xs text-muted-foreground'>{user?.role}</div>
                  </div>
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  className='w-full justify-start'
                  onClick={() => { setOpen(false); logout(); navigate('/'); }}
                  data-testid='sidebar-logout-mobile'
                >
                  <LogOut className='h-4 w-4 mr-2' /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>
        {/* Desktop top-bar — keeps the NotificationBell reachable from every
            authenticated page (Inquiries Inbox / My Listings / etc.). Mobile
            already has the bell in its own header above. */}
        <div className='hidden md:flex items-center justify-end h-12 px-4 lg:px-8 border-b bg-card/60'>
          <NotificationBell />
        </div>
        <main className='flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto'>
          <AnnouncementBanner />
          <Outlet />
        </main>
      </div>
      <GoToTop />
    </div>
  );
};

export default DashboardLayout;
